// Master Events Data Processor
// Handles categorization, geographic clustering, and data transformation

import { formatDateToISO } from '../utils/timeHelpers';

/**
 * Auto-categorization algorithm for events
 */
export const categorizeEvent = (event) => {
  const title = event.event_title.toLowerCase();
  const description = event.description.toLowerCase();
  const location = event['location/address'].toLowerCase();
  
  // Arts/Culture Detection
  if (title.includes('concert') || title.includes('music') || title.includes('theatre') || 
      title.includes('ballet') || title.includes('opera') || title.includes('museum') ||
      title.includes('art') || title.includes('gallery') || title.includes('exhibition') ||
      title.includes('film') || title.includes('cinema') || title.includes('dance') ||
      title.includes('orchestra') || title.includes('symphony') || title.includes('recital')) {
    return {
      primary: 'Arts/Culture',
      subcategory: getArtsSubcategory(title, description)
    };
  }
  
  // Social Detection
  if (title.includes('party') || title.includes('happy hour') || title.includes('networking') ||
      title.includes('reception') || title.includes('festival') || title.includes('social') ||
      title.includes('celebration') || title.includes('food') || title.includes('dining') ||
      title.includes('restaurant') || title.includes('beer') || title.includes('wine')) {
    return {
      primary: 'Social',
      subcategory: getSocialSubcategory(title, description)
    };
  }
  
  // Professional Detection
  if (title.includes('conference') || title.includes('seminar') || title.includes('briefing') ||
      title.includes('forum') || title.includes('panel') || title.includes('policy') ||
      title.includes('economic') || title.includes('trade') || title.includes('business') ||
      title.includes('debate') || title.includes('discussion') || title.includes('talk') ||
      title.includes('workshop') || title.includes('training') || title.includes('summit')) {
    return {
      primary: 'Professional',
      subcategory: getProfessionalSubcategory(title, description)
    };
  }
  
  // Wellness Detection
  if (title.includes('fitness') || title.includes('yoga') || title.includes('health') ||
      title.includes('wellness') || title.includes('sports') || title.includes('race') ||
      title.includes('volleyball') || title.includes('workout') || title.includes('gym') ||
      title.includes('running') || title.includes('marathon') || title.includes('tennis')) {
    return {
      primary: 'Wellness',
      subcategory: getWellnessSubcategory(title, description)
    };
  }
  
  // Education Detection
  if (title.includes('tour') || title.includes('guided') || title.includes('lecture') ||
      title.includes('class') || title.includes('education') || title.includes('learning') ||
      title.includes('study') || title.includes('research') || title.includes('academic')) {
    return {
      primary: 'Education',
      subcategory: getEducationSubcategory(title, description)
    };
  }
  
  // Default fallback to Social
  return {
    primary: 'Social',
    subcategory: 'General'
  };
};

/**
 * Subcategory classification functions
 */
const getArtsSubcategory = (title, description) => {
  if (title.includes('music') || title.includes('concert') || title.includes('orchestra') || 
      title.includes('symphony') || title.includes('recital')) return 'Music';
  if (title.includes('theatre') || title.includes('play') || title.includes('drama') || 
      title.includes('theater')) return 'Theatre';
  if (title.includes('museum') || title.includes('gallery') || title.includes('exhibition') ||
      title.includes('art')) return 'Museums';
  if (title.includes('dance') || title.includes('ballet')) return 'Dance';
  if (title.includes('film') || title.includes('cinema') || title.includes('movie')) return 'Film';
  if (title.includes('opera')) return 'Opera';
  return 'General';
};

const getSocialSubcategory = (title, description) => {
  if (title.includes('party') || title.includes('happy hour')) return 'Parties';
  if (title.includes('networking') || title.includes('reception')) return 'Networking';
  if (title.includes('festival') || title.includes('celebration')) return 'Festivals';
  if (title.includes('food') || title.includes('dining') || title.includes('restaurant') ||
      title.includes('beer') || title.includes('wine') || title.includes('cocktail')) return 'Food & Drink';
  if (title.includes('community') || title.includes('meetup')) return 'Community';
  return 'General';
};

const getProfessionalSubcategory = (title, description) => {
  // More comprehensive keyword matching for better categorization
  const titleLower = title.toLowerCase();
  const descLower = description.toLowerCase();
  const combined = `${titleLower} ${descLower}`;
  
  // Conferences and Summits
  if (titleLower.includes('conference') || titleLower.includes('summit') || 
      titleLower.includes('forum') || titleLower.includes('symposium') ||
      combined.includes('annual meeting') || combined.includes('convention')) {
    return 'Conferences';
  }
  
  // Workshops and Training
  if (titleLower.includes('workshop') || titleLower.includes('training') || 
      titleLower.includes('seminar') || titleLower.includes('bootcamp') ||
      titleLower.includes('masterclass') || titleLower.includes('course')) {
    return 'Workshops';
  }
  
  // Policy and Government
  if (titleLower.includes('briefing') || titleLower.includes('policy') || 
      titleLower.includes('government') || titleLower.includes('civic') ||
      titleLower.includes('public') || titleLower.includes('legislative') ||
      titleLower.includes('regulatory') || titleLower.includes('advocacy')) {
    return 'Policy';
  }
  
  // Networking and Social Professional Events
  if (titleLower.includes('networking') || titleLower.includes('reception') || 
      titleLower.includes('mixer') || titleLower.includes('happy hour') ||
      titleLower.includes('social') || titleLower.includes('meet') ||
      titleLower.includes('connect') || titleLower.includes('gathering') ||
      titleLower.includes('cocktail') || titleLower.includes('after hours')) {
    return 'Networking';
  }
  
  // Talks, Lectures, and Discussions
  if (titleLower.includes('talk') || titleLower.includes('lecture') || 
      titleLower.includes('discussion') || titleLower.includes('panel') ||
      titleLower.includes('keynote') || titleLower.includes('presentation') ||
      titleLower.includes('speaker') || titleLower.includes('roundtable') ||
      titleLower.includes('fireside') || titleLower.includes('interview')) {
    return 'Talks';
  }
  
  // Default fallback
  return 'General';
};

const getWellnessSubcategory = (title, description) => {
  if (title.includes('fitness') || title.includes('gym') || title.includes('workout')) return 'Fitness';
  if (title.includes('yoga') || title.includes('meditation') || title.includes('mindfulness')) return 'Mindfulness';
  if (title.includes('sports') || title.includes('race') || title.includes('volleyball') ||
      title.includes('running') || title.includes('marathon') || title.includes('tennis')) return 'Sports';
  if (title.includes('health') || title.includes('wellness')) return 'Health';
  if (title.includes('outdoor') || title.includes('hiking') || title.includes('walking')) return 'Outdoor';
  return 'General';
};

const getEducationSubcategory = (title, description) => {
  if (title.includes('tour') || title.includes('guided')) return 'Tours';
  if (title.includes('lecture') || title.includes('talk')) return 'Lectures';
  if (title.includes('class') || title.includes('workshop')) return 'Classes';
  if (title.includes('seminar')) return 'Seminars';
  if (title.includes('research') || title.includes('academic')) return 'Research';
  return 'General';
};

/**
 * Calculate distance from DC center (approximate)
 */
const calculateDistance = (coordinates) => {
  if (!coordinates || coordinates.length !== 2) return 'TBD';
  
  const [lng, lat] = coordinates;
  const dcCenter = [-77.0369, 38.9072];
  
  // Simple distance calculation (not precise, but good enough for display)
  const distance = Math.sqrt(
    Math.pow(lng - dcCenter[0], 2) + Math.pow(lat - dcCenter[1], 2)
  ) * 69; // Rough conversion to miles
  
  return distance < 1 ? '< 1 mile' : `${Math.round(distance)} miles`;
};

/**
 * Parse event date from start_time string
 */
const parseEventDate = (startTime) => {
  if (!startTime) return new Date();
  
  try {
    // Try to extract full date from formats like "Wednesday, Oct 26, 2025 • 2:00 PM"
    const fullDateMatch = startTime.match(/(\w+, \w+ \d+, \d{4})/);
    if (fullDateMatch) {
      const dateStr = fullDateMatch[1];
      const parsedDate = new Date(dateStr);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    
    // Fallback: try to extract month, day, and year separately
    const yearMatch = startTime.match(/(\d{4})/);
    const monthDayMatch = startTime.match(/(\w+) (\d+)/);
    
    if (yearMatch && monthDayMatch) {
      const year = yearMatch[1];
      const month = monthDayMatch[1];
      const day = monthDayMatch[2];
      const dateStr = `${month} ${day}, ${year}`;
      const parsedDate = new Date(dateStr);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    
    // Final fallback: try to extract just month and day with current year
    const dateMatch = startTime.match(/(\w+ \d+)/);
    if (dateMatch) {
      const dateStr = dateMatch[1];
      const currentYear = new Date().getFullYear();
      const parsedDate = new Date(`${dateStr}, ${currentYear}`);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
  } catch (error) {
    console.error('Error parsing event date:', error, 'from:', startTime);
  }
  
  // Return current date as fallback
  return new Date();
};

/**
 * Generate event URL from title
 */
const generateEventUrl = (title) => {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 50);
  return `https://hyyper.co/events/${slug}`;
};

/**
 * Extract tags from event title and description
 */
const extractTags = (title, description) => {
  const text = `${title} ${description}`.toLowerCase();
  const tags = [];
  
  const tagKeywords = {
    'music': ['concert', 'music', 'band', 'orchestra', 'symphony'],
    'art': ['art', 'gallery', 'museum', 'exhibition'],
    'food': ['food', 'dining', 'restaurant', 'beer', 'wine'],
    'fitness': ['fitness', 'gym', 'workout', 'sports'],
    'networking': ['networking', 'reception', 'meetup'],
    'free': ['free', 'no cost', 'complimentary'],
    'family': ['family', 'kids', 'children'],
    'outdoor': ['outdoor', 'park', 'garden', 'patio']
  };
  
  Object.entries(tagKeywords).forEach(([tag, keywords]) => {
    if (keywords.some(keyword => text.includes(keyword))) {
      tags.push(tag);
    }
  });
  
  return tags;
};

/**
 * Process master events with categorization and enhancement
 */
/**
 * Add sample image URLs to events based on category and title
 */
const addEventImages = (event) => {
  const category = event.categoryPrimary;
  const title = event.event_title?.toLowerCase() || '';
  
  // Event-specific image mappings
  const eventImages = {
    'concert': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=600&fit=crop&auto=format',
    'music': 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=600&fit=crop&auto=format',
    'theatre': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&auto=format',
    'museum': 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=600&h=600&fit=crop&auto=format',
    'art': 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=600&h=600&fit=crop&auto=format',
    'gallery': 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=600&h=600&fit=crop&auto=format',
    'conference': 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=600&fit=crop&auto=format',
    'seminar': 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=600&fit=crop&auto=format',
    'party': 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=600&h=600&fit=crop&auto=format',
    'festival': 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=600&h=600&fit=crop&auto=format',
    'fitness': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=600&fit=crop&auto=format',
    'yoga': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=600&fit=crop&auto=format',
    'health': 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&h=600&fit=crop&auto=format'
  };
  
  // Find matching image based on event title keywords
  for (const [keyword, imageUrl] of Object.entries(eventImages)) {
    if (title.includes(keyword)) {
      return { ...event, image_url: imageUrl };
    }
  }
  
  return event;
};

export const processMasterEvents = (rawEvents) => {
  return rawEvents.map((event, index) => {
    const category = categorizeEvent(event);
    
    const processedEvent = {
      id: `master-event-${index}`,
      name: event.event_title,
      event_title: event.event_title, // Add for map component compatibility
      description: event.description,
      time: event.start_time,
      start_time: event.start_time, // Add for map component compatibility
      venue: event['location/address'],
      address: event['location/address'],
      'location/address': event['location/address'], // Add for map component compatibility
      coordinates: [event.longitude, event.latitude],
      latitude: event.latitude, // Add individual properties for map component
      longitude: event.longitude, // Add individual properties for map component
      categoryPrimary: category.primary,
      categorySecondary: category.subcategory,
      format: event.format,
      isOnline: event.format === 'Online only',
      isPrivate: event.format === 'Private (invite-only)',
      price: 'TBD', // Default pricing - could be enhanced with price detection
      attendees: Math.floor(Math.random() * 500) + 10, // Random attendance for demo
      distance: calculateDistance([event.longitude, event.latitude]),
      date: formatDateToISO(parseEventDate(event.start_time)),
      endTime: null, // Could be extracted from start_time if available
      url: generateEventUrl(event.event_title),
      tags: extractTags(event.event_title, event.description),
      // Additional metadata
      source: 'master-events',
      isRealEvent: true,
      locationType: getLocationType(event['location/address']),
      eventType: getEventType(event.event_title, event.description)
    };
    
    // Add event-specific images
    return addEventImages(processedEvent);
  });
};

/**
 * Determine location type from address
 */
const getLocationType = (address) => {
  const addr = address.toLowerCase();
  if (addr.includes('kennedy center') || addr.includes('theater') || addr.includes('theatre')) return 'venue';
  if (addr.includes('museum') || addr.includes('gallery')) return 'museum';
  if (addr.includes('park') || addr.includes('garden')) return 'outdoor';
  if (addr.includes('restaurant') || addr.includes('bar')) return 'dining';
  if (addr.includes('online') || addr.includes('virtual')) return 'online';
  return 'venue';
};

/**
 * Determine event type from title and description
 */
const getEventType = (title, description) => {
  const text = `${title} ${description}`.toLowerCase();
  if (text.includes('conference') || text.includes('summit')) return 'conference';
  if (text.includes('workshop') || text.includes('class')) return 'workshop';
  if (text.includes('tour') || text.includes('guided')) return 'tour';
  if (text.includes('festival') || text.includes('celebration')) return 'festival';
  if (text.includes('party') || text.includes('happy hour')) return 'social';
  return 'event';
};

/**
 * Cluster events by location for better organization
 */
export const clusterEventsByLocation = (events) => {
  const clusters = {
    'Downtown DC': [],
    'Georgetown': [],
    'Adams Morgan': [],
    'U Street': [],
    'The Wharf': [],
    'Capitol Hill': [],
    'Dupont Circle': [],
    'Other': []
  };
  
  events.forEach(event => {
    const address = event.address.toLowerCase();
    if (address.includes('downtown') || address.includes('pennsylvania ave') || 
        address.includes('k street') || address.includes('connecticut ave')) {
      clusters['Downtown DC'].push(event);
    } else if (address.includes('georgetown') || address.includes('wisconsin ave')) {
      clusters['Georgetown'].push(event);
    } else if (address.includes('adams morgan') || address.includes('18th st')) {
      clusters['Adams Morgan'].push(event);
    } else if (address.includes('u street') || address.includes('14th st') || 
               address.includes('u st')) {
      clusters['U Street'].push(event);
    } else if (address.includes('wharf') || address.includes('maine ave') || 
               address.includes('southwest')) {
      clusters['The Wharf'].push(event);
    } else if (address.includes('capitol') || address.includes('east capitol') || 
               address.includes('southeast')) {
      clusters['Capitol Hill'].push(event);
    } else if (address.includes('dupont') || address.includes('connecticut ave') || 
               address.includes('massachusetts ave')) {
      clusters['Dupont Circle'].push(event);
    } else {
      clusters['Other'].push(event);
    }
  });
  
  return clusters;
};

/**
 * Filter events by various criteria
 */
export const filterEvents = (events, filters) => {
  return events.filter(event => {
    const matchesCategory = !filters.category || event.categoryPrimary === filters.category;
    const matchesSubcategory = !filters.subcategory || event.categorySecondary === filters.subcategory;
    const matchesFormat = !filters.format || event.format === filters.format;
    const matchesLocation = !filters.location || event.address.toLowerCase().includes(filters.location.toLowerCase());
    const isNotPrivate = !filters.excludePrivate || !event.isPrivate;
    const hasValidLocation = event.coordinates && event.coordinates.length === 2;
    
    return matchesCategory && matchesSubcategory && matchesFormat && 
           matchesLocation && isNotPrivate && hasValidLocation;
  });
};

/**
 * Search events by text query
 */
export const searchEvents = (events, query) => {
  if (!query) return events;
  
  const searchTerm = query.toLowerCase();
  return events.filter(event => 
    event.name.toLowerCase().includes(searchTerm) ||
    event.description.toLowerCase().includes(searchTerm) ||
    event.venue.toLowerCase().includes(searchTerm) ||
    event.tags.some(tag => tag.includes(searchTerm))
  );
};
